import { acceptHMRUpdate, defineStore } from 'pinia';
import { inject } from 'vue';
import type {
  LightingAdvisorQuestion,
  LightingAdvisorRecommendationFlag,
  LightingAdvisorTileId,
  LightingAnswers
} from '~/types/lightingAdvisor';
import { useLightingAdvisor } from '~/composables/useLightingAdvisor';
import type { useProductFilterStore } from '~/stores/productFilterStore/productFilterStore';
import { LIGHTING_ADVISOR_QUESTION_IDS_BY_TILE } from '~/utils/lightingAdvisor/constants';
import { hasAnyLightingAnswer } from '~/utils/lightingAdvisor/hasAnyLightingAnswer';
import { pickRecommendedLightingProducts } from '~/utils/lightingAdvisor/pickRecommendedLightingSkus';

export type LightingBadgeBySku = Record<
  string,
  LightingAdvisorRecommendationFlag
>;

export const LIGHTING_ADVISOR_STORE_INJECT_KEY = Symbol(
  'lighting-advisor-store'
);

type ProductFilterStore = ReturnType<typeof useProductFilterStore>;

type LightingAdvisorStoreDefinition = ReturnType<
  typeof createLightingAdvisorStore
>;

/** Section -> question ids (incl. the synthetic dimension inputs of tile 2). */
export const LIGHTING_ADVISOR_SECTIONS: ReadonlyArray<{
  tileId: LightingAdvisorTileId;
  sectionId: 'a2s1' | 'a2s2' | 'a2s3';
}> = [
  { tileId: 'tile1', sectionId: 'a2s1' },
  { tileId: 'tile2', sectionId: 'a2s2' },
  { tileId: 'tile3', sectionId: 'a2s3' }
];

/** Stable English labels for GA (locale-independent). */
export const LIGHTING_ADVISOR_SECTION_ANALYTICS_LABELS: Record<
  'a2s1' | 'a2s2' | 'a2s3',
  string
> = {
  a2s1: 'Application',
  a2s2: 'Dimensions',
  a2s3: 'Color'
};

function sectionQuestionIds(tileId: LightingAdvisorTileId): string[] {
  const ids = [...LIGHTING_ADVISOR_QUESTION_IDS_BY_TILE[tileId]];
  if (tileId === 'tile2') {
    ids.push('a2s2q2_length', 'a2s2q2_width');
  }
  return ids;
}

function isSectionAnswered(
  tileId: LightingAdvisorTileId,
  currentAnswers: LightingAnswers
): boolean {
  return sectionQuestionIds(tileId).some((questionId) => {
    const value = currentAnswers[questionId];
    return value != null && String(value).trim() !== '';
  });
}

function activeSectionsFromAnswers(
  currentAnswers: LightingAnswers
): Array<(typeof LIGHTING_ADVISOR_SECTIONS)[number]> {
  return LIGHTING_ADVISOR_SECTIONS.filter(({ tileId }) =>
    isSectionAnswered(tileId, currentAnswers)
  );
}

export const useLightingAdvisorStore = (): LightingAdvisorStoreDefinition => {
  const store = inject<LightingAdvisorStoreDefinition>(
    LIGHTING_ADVISOR_STORE_INJECT_KEY
  );
  if (!store) {
    throw new Error('no lighting advisor store provided');
  }
  return store;
};

export function createLightingAdvisorStore(
  id: string,
  productFilterStore: ProductFilterStore
) {
  const store = defineStore(`lighting-advisor-${id}`, () => {
    const advisor = useLightingAdvisor();
    const answers = ref<LightingAnswers>({});
    const recommendedSkus = ref<string[]>([]);
    const recommendedFlagBySku = ref<LightingBadgeBySku>({});
    // True when the advisor was used but the API returned no recommended products.
    const noRecommendations = ref(false);
    const initialLoadError = ref(false);

    const flow = advisor.useLightingQuestionFlow(answers);

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    function cancelScheduledApply() {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
    }

    function scheduleApply(delay = 450) {
      cancelScheduledApply();
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        void fetchAndApply();
      }, delay);
    }

    async function fetchAndApply() {
      if (!hasAnyLightingAnswer(answers.value)) {
        recommendedSkus.value = [];
        recommendedFlagBySku.value = {};
        noRecommendations.value = false;
        await productFilterStore.clearLightingSkuFilter({ reload: true });
        return;
      }
      const response = await advisor.fetchRecommendations(answers.value);
      if (!response) {
        return;
      }
      const products = pickRecommendedLightingProducts(
        response.recommended_products
      );
      const skus = products.map((product) => product.sku);
      recommendedSkus.value = skus;
      recommendedFlagBySku.value = Object.fromEntries(
        products.flatMap((product) => {
          const flag = product.flags?.includes('best_technical_fit')
            ? 'best_technical_fit'
            : product.flags?.includes('price_performance_ratio')
              ? 'price_performance_ratio'
              : null;
          return flag ? [[product.sku, flag]] : [];
        })
      );
      if (skus.length > 0) {
        noRecommendations.value = false;
        await productFilterStore.setLightingSkuFilter(skus);
      } else {
        // Advisor was used but matched no products: show an empty list + hint
        // instead of falling back to the full, unfiltered product list.
        noRecommendations.value = true;
        await productFilterStore.setLightingNoMatch();
      }
      advisor.trackEvent('lightingAdvisor_recommendationsReady', {
        entry: 'plp',
        result_count: skus.length,
        sections: activeSectionsFromAnswers(answers.value).map(
          ({ sectionId }) =>
            LIGHTING_ADVISOR_SECTION_ANALYTICS_LABELS[sectionId]
        )
      });
    }

    async function loadQuestions() {
      const response = await advisor.loadQuestions();
      initialLoadError.value = !response?.questions?.length;
      return response;
    }

    function setAnswer(questionId: string, value: string | null) {
      flow.setAnswer(questionId, value);
      scheduleApply();
    }

    function resetSection(tileId: LightingAdvisorTileId) {
      advisor.trackEvent('lightingAdvisor_reset', { tile: tileId });
      const next = { ...answers.value };
      for (const questionId of sectionQuestionIds(tileId)) {
        delete next[questionId];
      }
      answers.value = next;
      scheduleApply(0);
    }

    async function resetAll(options?: { reloadProducts?: boolean }) {
      cancelScheduledApply();
      answers.value = {};
      recommendedSkus.value = [];
      recommendedFlagBySku.value = {};
      noRecommendations.value = false;
      await productFilterStore.clearLightingSkuFilter({
        reload: options?.reloadProducts ?? true
      });
    }

    const activeSections = computed(() =>
      activeSectionsFromAnswers(answers.value)
    );

    // A regular product filter change resets the lighting selection. The filter
    // change reloads the list itself, so we skip the extra fetch here.
    productFilterStore.registerLightingReset(() => {
      void resetAll({ reloadProducts: false });
    });

    return {
      answers,
      recommendedSkus,
      recommendedFlagBySku,
      noRecommendations,
      initialLoadError,
      questions: advisor.questions,
      isApplying: advisor.isLoading,
      lastResults: advisor.lastResults,
      activeSections,
      questionsForTile: (prefix: 'a2s1' | 'a2s2' | 'a2s3') =>
        flow.questionsForTile(prefix),
      isQuestionVisible: (questionId: string) =>
        flow.isQuestionVisible(questionId),
      getFilteredInputs: (question: LightingAdvisorQuestion) =>
        flow.getFilteredInputs(question),
      getTooltipKeys: (question: LightingAdvisorQuestion) =>
        flow.getTooltipKeys(question),
      getWarningNotes: (question: LightingAdvisorQuestion) =>
        flow.getWarningNotes(question),
      loadQuestions,
      setAnswer,
      resetSection,
      resetAll
    };
  });

  if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
  }

  return store();
}
